import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2, Clock, ArrowRight, AlertTriangle, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

export default function SigneringRetur() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const type = sp.get("type");
  const id = sp.get("id");
  const company = sp.get("company");
  const [st, setSt] = useState({ loading: true, status: null, executed: false, signedAvailable: false, error: null });

  useEffect(() => {
    if (!type || !id) {
      setSt({ loading: false, error: "Mangler informasjon om signeringen." });
      return;
    }
    api
      .post(`/signing/${type}/${id}/sync`)
      .then(({ data }) =>
        setSt({ loading: false, status: data.status, executed: data.executed, signedAvailable: data.signed_available, error: null })
      )
      .catch((e) => setSt({ loading: false, error: formatApiError(e.response?.data?.detail) }));
  }, [type, id]);

  const back = () => {
    if (company) {
      navigate(type === "meeting" ? `/selskap/${company}/moter/${id}` : `/selskap/${company}/aksjehandbok`);
    } else {
      navigate("/hjem");
    }
  };

  const done = st.status === "completed" || st.executed;
  const pending = !st.error && !done && (st.status === "open" || st.status === "none" || st.status === "gone");

  return (
    <div className="grid min-h-screen place-items-center bg-secondary/30 px-5" data-testid="signering-retur-page">
      <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-8 text-center shadow-sm">
        <div className="mb-6 flex justify-center"><Logo /></div>

        {st.loading ? (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="signering-loading">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Behandler BankID-signeringen …</p>
          </div>
        ) : st.error ? (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="signering-error">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-amber-500/10 text-amber-600"><AlertTriangle className="h-7 w-7" /></div>
            <h1 className="font-headings text-xl font-bold">Noe gikk galt</h1>
            <p className="text-sm text-muted-foreground">{st.error}</p>
          </div>
        ) : done ? (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="signering-done">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-emerald-500/10 text-emerald-600"><CheckCircle2 className="h-7 w-7" /></div>
            <h1 className="font-headings text-xl font-bold">Signeringen er fullført</h1>
            <p className="text-sm text-muted-foreground">Dokumentet er signert med BankID.</p>
            {st.signedAvailable ? (
              <Button
                variant="outline"
                className="mt-1 gap-2"
                data-testid="signering-download-signed"
                onClick={() => openSigned(`/signing/${type}/${id}/signed-link`).catch(() => toast.error("Kunne ikke åpne det signerte dokumentet"))}
              >
                <FileDown className="h-4 w-4" /> Se BankID-signert PDF
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="signering-pending">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-primary/10 text-primary"><Clock className="h-7 w-7" /></div>
            <h1 className="font-headings text-xl font-bold">Signaturen er registrert</h1>
            <p className="text-sm text-muted-foreground">
              {pending ? "Vi venter på at de øvrige partene signerer med BankID." : "Signeringen er registrert."}
            </p>
          </div>
        )}

        <Button className="mt-6 w-full gap-2" onClick={back} data-testid="signering-back-btn">
          Tilbake til dokumentet <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
